// Translates common NestJS / class-validator error messages from the backend (which arrive
// in English) into Spanish, so coaches don't see "Bad Request" or "must be a valid email"
// in toasts. Falls back to the original message if no rule matches.
//
// Used from `src/services/api-client.ts` right before throwing the error to the caller.
// Card: Trello #21B (Lenguaje — errores BE en español).

const HTTP_STATUS_MAP: Record<number, string> = {
  400: 'Solicitud inválida.',
  401: 'No autorizado. Volvé a iniciar sesión.',
  403: 'No tenés permisos para esta acción.',
  404: 'Recurso no encontrado.',
  409: 'Conflicto: el recurso ya existe o está en uso.',
  413: 'El archivo es demasiado grande.',
  422: 'La información enviada no es válida.',
  429: 'Demasiados intentos. Esperá unos segundos.',
  500: 'Error interno del servidor. Intentá de nuevo más tarde.',
  502: 'El servidor no está disponible. Intentá más tarde.',
  503: 'Servicio temporalmente no disponible.',
  504: 'El servidor tardó demasiado en responder.'
};

// Exact-match (case-insensitive) lookups for the most common backend messages.
const EXACT_MAP: Record<string, string> = {
  unauthorized: 'No autorizado.',
  forbidden: 'No tenés permisos para esta acción.',
  'forbidden resource': 'No tenés permisos para esta acción.',
  'not found': 'Recurso no encontrado.',
  'bad request': 'Solicitud inválida.',
  'internal server error': 'Error interno del servidor. Intentá de nuevo más tarde.',
  'invalid credentials': 'Correo electrónico o contraseña incorrectos.',
  'wrong credentials': 'Correo electrónico o contraseña incorrectos.',
  'invalid email or password': 'Correo electrónico o contraseña incorrectos.',
  'user not found': 'Usuario no encontrado.',
  'email already exists': 'Ya existe una cuenta con ese correo electrónico.',
  'email already registered': 'Ya existe una cuenta con ese correo electrónico.',
  // Alta de alumnos y de coaches (#21). Los mensajes salen tal cual de
  // be-coaching/src/user/user.service.ts; el primero es el 409 que se ve en los
  // logs de prod cuando un coach carga un alumno con un mail que ya existe.
  'already exists an user with that email': 'Ya existe una cuenta con ese correo electrónico.',
  'user already exists': 'Ya existe una cuenta con ese correo electrónico.',
  'coach already exists': 'Ya existe una cuenta de coach con ese correo electrónico.',
  'user not created': 'No pudimos crear el usuario. Intentá de nuevo.',
  'coach not found': 'No encontramos al coach.',
  'user does not exist': 'Usuario no encontrado.',
  'error signing up': 'No pudimos completar el registro. Intentá de nuevo.',
  'user type not valid': 'El tipo de usuario no es válido.',
  'email not verified': 'Tu correo no está verificado. Revisá tu bandeja.',
  'invalid verification code': 'El código de verificación no es válido.',
  'verification code expired': 'El código de verificación expiró. Pedí uno nuevo.',
  'invalid token': 'Tu sesión expiró. Volvé a iniciar sesión.',
  'token expired': 'Tu sesión expiró. Volvé a iniciar sesión.',
  'validation failed': 'La información ingresada no es válida.',
  'password too short': 'La contraseña es demasiado corta.',
  'subscription required': 'Necesitás una suscripción activa para continuar.',
  'too many requests': 'Demasiados intentos. Esperá unos segundos.'
};

// Regex patterns for class-validator messages that include field names.
// First capture group is normally the field name (kept inline if present).
const REGEX_RULES: Array<{ re: RegExp; render: (m: RegExpMatchArray) => string }> = [
  { re: /^(.+?) must be a valid email$/i, render: (m) => `${m[1]} debe ser un correo válido.` },
  { re: /^(.+?) should not be empty$/i, render: (m) => `${m[1]} es obligatorio.` },
  { re: /^(.+?) must not be empty$/i, render: (m) => `${m[1]} es obligatorio.` },
  { re: /^(.+?) must be a string$/i, render: (m) => `${m[1]} debe ser texto.` },
  // Order matters: more specific rules MUST come before generic ones (the generic
  // /must be a number/ would otherwise swallow "must be a positive number").
  { re: /^(.+?) must be a positive number$/i, render: (m) => `${m[1]} debe ser un número positivo.` },
  { re: /^(.+?) must be a number/i, render: (m) => `${m[1]} debe ser un número.` },
  {
    re: /^(.+?) must be longer than or equal to (\d+) characters?$/i,
    render: (m) => `${m[1]} debe tener al menos ${m[2]} caracteres.`
  },
  {
    re: /^(.+?) must be shorter than or equal to (\d+) characters?$/i,
    render: (m) => `${m[1]} no puede tener más de ${m[2]} caracteres.`
  },
  { re: /^(.+?) must be one of the following values: (.+)$/i, render: (m) => `${m[1]} debe ser uno de: ${m[2]}.` },
  { re: /^(.+?) must be a valid date/i, render: (m) => `${m[1]} debe ser una fecha válida.` },
  { re: /^(.+?) must be an? (?:integer|int)$/i, render: (m) => `${m[1]} debe ser un número entero.` },
  { re: /^(.+?) must be a boolean value$/i, render: (m) => `${m[1]} debe ser verdadero o falso.` },
  { re: /^property (.+?) should not exist$/i, render: (m) => `Campo no permitido: ${m[1]}.` },
  { re: /^(.+?) is not a valid (.+)$/i, render: (m) => `${m[1]} no es válido.` }
];

/**
 * Translate a backend error message (and optional HTTP status) into Spanish.
 * Returns the original `message` unchanged if no rule matches and no status fallback applies.
 */
export function translateBackendError(message: string | undefined | null, status?: number): string {
  const raw = (message || '').trim();

  if (raw) {
    const lower = raw.toLowerCase();
    if (EXACT_MAP[lower]) return EXACT_MAP[lower];

    // Los mensajes de `user.service.ts` vienen con puntuación final inconsistente
    // ('User not found' vs 'User not found!' vs 'Coach not found.'), así que se
    // reintenta sin ella antes de dar por perdido el match (#21).
    const unpunctuated = lower.replace(/[.!?]+$/, '');
    if (unpunctuated !== lower && EXACT_MAP[unpunctuated]) return EXACT_MAP[unpunctuated];

    for (const { re, render } of REGEX_RULES) {
      const m = raw.match(re);
      if (m) return render(m);
    }
    // Caller gave a real message we don't know how to map: respect it as-is rather than
    // overriding with a generic status fallback (the BE message is more specific).
    return raw;
  }

  if (status && HTTP_STATUS_MAP[status]) return HTTP_STATUS_MAP[status];
  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}

/**
 * Translate an array of error messages and join them with " · " for display in a single toast.
 */
export function translateBackendErrors(messages: string[], status?: number): string {
  if (!messages || messages.length === 0) {
    return translateBackendError('', status);
  }
  return messages.map((m) => translateBackendError(m, status)).join(' · ');
}
